/**
 * canvas-engine/presets/decompositionAdapters/retail.js
 *
 * Retail (storefront) family adapter. Retail sections decompose faithfully with
 * the core engine's vertical-stack layout (category/product grids, cart and
 * summary shells, promo banners), so most delegate straight to
 * `buildSectionCanvas`.
 *
 * Family tweak: two-column sections that carry a single foreground image (the
 * product detail and "about store" sections) read best with the image beside
 * the lead copy — the shared split hook handles that. The multi-image hero
 * carousel falls through to an editable image grid in the default stack.
 *
 * The adapter accepts EITHER a transcribed `RETAIL_PRESETS` descriptor OR a
 * section-type string looked up in `RETAIL_PRESETS`, laying out at the fixed
 * `Reference_Width` with `idPrefix = sectionType`.
 *
 * Auth variant passthrough: `options.authVariant` (e.g. `'signUp'`) is forwarded
 * unchanged to `buildSectionCanvas` via `adapterOptions` (which spreads `...opts`).
 * When supplied, `buildSectionCanvas` merges `content.variants[authVariant]` over
 * `content` before layout, so `decomposeSection('retailAuth', { authVariant:
 * 'signUp' })` decomposes the `retailAuth` descriptor's `content.variants.signUp`
 * for the sign-up page (signIn is the default when no variant is passed). No
 * Retail-specific wiring is needed — the spread in `adapterOptions` already
 * carries the option through.
 *
 * Type coverage is automatic: `RETAIL_TYPES` is auto-derived from
 * `RETAIL_PRESETS` via `handledTypesOf`, so the `retailCategory` and `retailAuth`
 * presets are handled without any manual list edits here.
 *
 * ZERO-REUSE CONSTRAINT: imports ONLY sibling engine modules + transcribed
 * presetData VALUES. Never emits a `native` element.
 *
 * Requirements: 4.1, 5.1, 5.5, 5.6, 5.7, 3.7, 3.1, 3.4
 */

import { REFERENCE_FRAME_WIDTH } from './buildSectionCanvas';
import { buildFamilyCanvas, wantsSplit } from './familyLayout';
import { resolvePreset, adapterOptions, handledTypesOf } from './resolvePreset';
import { RETAIL_PRESETS } from '../presetData/retailPresets';

/** The Retail section types this adapter handles (keys + sectionTypes). */
export const RETAIL_TYPES = Object.freeze(handledTypesOf(RETAIL_PRESETS));

/** True when `sectionType` is decomposed by the Retail adapter. */
export function handlesRetailType(sectionType) {
  return RETAIL_TYPES.includes(typeof sectionType === 'string' ? sectionType.trim() : sectionType);
}

/**
 * Decompose a Retail section into a normalized Canvas_Model.
 *
 * @param {(string|object)} sectionTypeOrData a `RETAIL_PRESETS` key /
 *   sectionType, or a transcribed Retail preset DATA descriptor
 * @param {object} [options] `buildSectionCanvas` options; notably
 *   `options.authVariant` is forwarded (via `adapterOptions`) so `retailAuth`
 *   can decompose its `content.variants.signUp` block for the sign-up page
 * @returns {object} a normalized Canvas_Model
 */
export function retailToCanvas(sectionTypeOrData, options = {}) {
  const { data, sectionType } = resolvePreset(sectionTypeOrData, RETAIL_PRESETS, options);
  // `adapterOptions` spreads `...options`, so `authVariant` (and any other
  // caller option) is threaded through to `buildSectionCanvas` unchanged.
  const opts = adapterOptions(sectionType, REFERENCE_FRAME_WIDTH, options);
  return buildFamilyCanvas(data, opts, { split: wantsSplit(data) });
}

export default retailToCanvas;
